import { combineReducers } from '@reduxjs/toolkit'
import type { BoardRecurringTasksSliceState } from './boardRecurringTasks/boardRecurringTasks.types'
import boardRecurringTasksReducer from './boardRecurringTasks/boardRecurringTasks.slice'
import type { BoardAutomationsSliceState } from './boardAutomations/boardAutomations.types'
import boardAutomationsReducer from './boardAutomations/boardAutomations.slice'
import type { BoardEditSliceState } from './boardEdit/boardEdit.types'
import boardEditReducer from './boardEdit/boardEdit.slice'
import type { BoardFilterStickersSliceState } from './boardFilterStickers/boardFilterStickers.types'
import boardFilterStickersReducer from './boardFilterStickers/boardFilterStickers.slice'
import type { BoardKanbanSliceState } from './boardKanban/boardKanban.types'
import boardKanbanReducer from './boardKanban/boardKanban.slice'
import type { BoardStickerFiltersSliceState } from './boardStickerFilters/boardStickerFilters.types'
import boardStickerFiltersReducer from './boardStickerFilters/boardStickerFilters.slice'
import type { BoardStickersTabSliceState } from './boardStickersTab/boardStickersTab.types'
import boardStickersTabReducer from './boardStickersTab/boardStickersTab.slice'
import type { ColumnPaginationSliceState } from './columnPagination/columnPagination.types'
import columnPaginationReducer from './columnPagination/columnPagination.slice'
import type { ColumnSortSliceState } from './columnSort/columnSort.types'
import columnSortReducer from './columnSort/columnSort.slice'
import type { FiltersCatalogSliceState } from './filtersCatalog/filtersCatalog.types'
import filtersCatalogReducer from './filtersCatalog/filtersCatalog.slice'
import type { ProjectEditSliceState } from './projectEdit/projectEdit.types'
import projectEditReducer from './projectEdit/projectEdit.slice'
import type { ProjectMembersSliceState } from './projectMembers/projectMembers.types'
import projectMembersReducer from './projectMembers/projectMembers.slice'
import type { ProjectsSliceState } from './projects/projects.types'
import projectsReducer from './projects/projects.slice'
import type { SidebarSliceState } from './sidebar/sidebar.types'
import sidebarReducer from './sidebar/sidebar.slice'
import type { TaskDrawerSliceState } from './taskDrawer/taskDrawer.types'
import taskDrawerReducer from './taskDrawer/taskDrawer.slice'

export type TaskTrackerState = {
  projects: ProjectsSliceState
  boardKanban: BoardKanbanSliceState
  boardStickerFilters: BoardStickerFiltersSliceState
  boardFilterStickers: BoardFilterStickersSliceState
  boardEdit: BoardEditSliceState
  boardStickersTab: BoardStickersTabSliceState
  boardAutomations: BoardAutomationsSliceState
  boardRecurringTasks: BoardRecurringTasksSliceState
  columnPagination: ColumnPaginationSliceState
  columnSort: ColumnSortSliceState
  taskDrawer: TaskDrawerSliceState
  projectEdit: ProjectEditSliceState
  filtersCatalog: FiltersCatalogSliceState
  projectMembers: ProjectMembersSliceState
  sidebar: SidebarSliceState
}

const taskTrackerReducer = combineReducers({
  projects: projectsReducer,
  boardKanban: boardKanbanReducer,
  boardStickerFilters: boardStickerFiltersReducer,
  boardFilterStickers: boardFilterStickersReducer,
  boardEdit: boardEditReducer,
  boardStickersTab: boardStickersTabReducer,
  boardAutomations: boardAutomationsReducer,
  boardRecurringTasks: boardRecurringTasksReducer,
  columnPagination: columnPaginationReducer,
  columnSort: columnSortReducer,
  taskDrawer: taskDrawerReducer,
  projectEdit: projectEditReducer,
  filtersCatalog: filtersCatalogReducer,
  projectMembers: projectMembersReducer,
  sidebar: sidebarReducer,
})

export { removeBoard } from './shared/boardCleanup.actions'

export {
  setActiveProjectId,
  clearCreateProjectError,
  updateProjectTitle,
  removeProject,
  addProjectBoard,
  updateBoardTitle,
} from './projects/projects.slice'

export {
  bumpBoardReloadToken,
  clearBoardKanban,
  setBoardItems,
  setBoardColumns,
  removeBoardCard,
} from './boardKanban/boardKanban.slice'

export {
  switchBoardStickerFilters,
  setBoardStickerDraftFilter,
  setBoardCompletedStatusFilter,
  resetBoardStickerFilters,
  pruneBoardStickerFilters,
  syncBoardStickerAppliedFilters,
} from './boardStickerFilters/boardStickerFilters.slice'

export { bumpBoardFilterStickersReloadToken } from './boardFilterStickers/boardFilterStickers.slice'

export {
  resetBoardEditState,
  setBoardEditBoardTitle,
  setBoardEditColumnDraftField,
  setBoardEditNewColumnTitle,
  setBoardEditNewColumnColor,
} from './boardEdit/boardEdit.slice'

export {
  setBoardAutomationsOnCompleteDraft,
  setBoardAutomationsOnMoveDraft,
} from './boardAutomations/boardAutomations.slice'

export {
  resetColumnSort,
  syncColumnSortColumns,
  setColumnSort,
} from './columnSort/columnSort.slice'

export {
  resetColumnPagination,
  setColumnPaginationItem,
  syncColumnPaginationColumns,
} from './columnPagination/columnPagination.slice'

export {
  setTaskDrawerSuppressedTaskId,
  clearTaskDrawerOverride,
} from './taskDrawer/taskDrawer.slice'

export {
  setProjectEditTitle,
  toggleProjectEditRolePermission,
  resetProjectEditState,
  optimisticUpdateProjectMemberRole,
  revertProjectEditMembers,
} from './projectEdit/projectEdit.slice'

export { applyFiltersCatalog } from './filtersCatalog/filtersCatalog.slice'

export { toggleSidebarCollapsed, setSidebarCollapsed } from './sidebar/sidebar.slice'

export default taskTrackerReducer
